import React from 'react';
import type { Question, Language } from '@nordcrest/shared';

interface MultiSelectProps {
  question: Question;
  value: string[];
  onChange: (value: string[]) => void;
  language: Language;
  error?: string;
}

export function MultiSelect({ question, value, onChange, language, error }: MultiSelectProps) {
  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      // If selecting 'none', clear other selections
      if (optionValue === 'none') {
        onChange([]);
      } else {
        onChange(value.filter((v) => v !== optionValue));
      }
    } else {
      // If selecting something other than 'none', remove 'none' from selection
      if (optionValue === 'none') {
        onChange(['none']);
      } else {
        onChange([...value.filter((v) => v !== 'none'), optionValue]);
      }
    }
  };

  return (
    <div className="space-y-3">
      {question.options?.map((option) => {
        const isSelected = value.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggleOption(option.value)}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
              isSelected
                ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50/50'
            }`}
          >
            <div className="flex items-center">
              <div
                className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                  isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                }`}
              >
                {isSelected && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              <div>
                <span className="font-medium text-gray-900">{option.label[language]}</span>
                {option.description && (
                  <p className="text-sm text-gray-500 mt-0.5">{option.description[language]}</p>
                )}
              </div>
            </div>
          </button>
        );
      })}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
